'use client';

import React, { ReactNode } from 'react';
import { useWorkspace } from '@/contexts/WorkspaceProvider';
import { useModuleSubscriptions } from '@/hooks/useSubscription';
import { LucideIcon, Lock, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useRouter } from 'next/navigation';

export type MinPlan = 'prospect' | 'outreach' | 'growth' | 'enterprise' | 'starter' | 'standard' | 'pro';

const PLAN_RANK: Record<string, number> = {
  // Current plan keys (v2 pricing, 2026-06-14)
  prospect: 1,
  outreach: 2,
  growth: 3,
  enterprise: 4,
  // Legacy aliases — kept so existing DB rows with old plan names still gate correctly
  starter: 1,
  kickstart: 1,
  standard: 2,
  professional: 2,
  trial: 2,
  pro: 3,
};

const PLAN_LABELS: Record<string, string> = {
  prospect: 'Prospect',
  outreach: 'Outreach',
  growth: 'Growth',
  enterprise: 'Enterprise',
  // Legacy labels for backwards-compat
  starter: 'Prospect',
  standard: 'Outreach',
  pro: 'Growth',
};

function planMeetsMinimum(currentPlan: string | undefined, minPlan: MinPlan): boolean {
  if (!currentPlan) return false;
  const currentRank = PLAN_RANK[currentPlan] ?? 0;
  const minRank = PLAN_RANK[minPlan] ?? 99;
  return currentRank >= minRank;
}

interface PlanGateProps {
  requiredPlan: MinPlan;
  children: ReactNode;
  title?: string;
  description?: string;
  icon?: LucideIcon;
  showLoading?: boolean;
}

// GH#2082: Use the highest-ranked plan across both modules so marketing-only enterprise
// workspaces (Pharos AI pattern: active sub keyed 'marketing') and sales-only enterprise
// workspaces (Parks Associates pattern: active sub keyed 'sales' with product_modules={marketing})
// both pass correctly without needing per-feature productModule declarations.
export const PlanGate: React.FC<PlanGateProps> = ({
  requiredPlan,
  children,
  title,
  description,
  icon: Icon = Lock,
  showLoading = true,
}) => {
  const workspaceContext = useWorkspace();
  const { data: moduleSubData, isLoading: subLoading } = useModuleSubscriptions({
    enabled: !!workspaceContext?.activeWorkspace,
  });
  const router = useRouter();

  const salesRank = PLAN_RANK[moduleSubData?.data?.sales?.plan ?? ''] ?? 0;
  const marketingRank = PLAN_RANK[moduleSubData?.data?.marketing?.plan ?? ''] ?? 0;
  const effectiveRank = Math.max(salesRank, marketingRank);
  const currentPlan =
    effectiveRank === salesRank
      ? moduleSubData?.data?.sales?.plan
      : moduleSubData?.data?.marketing?.plan;

  if (showLoading && (!workspaceContext || workspaceContext.isLoading || subLoading)) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] py-12 px-4 text-center">
        <Loader2 className="w-8 h-8 text-slate-400 animate-spin mb-4" />
        <p className="text-sm text-slate-500">Loading...</p>
      </div>
    );
  }

  if (planMeetsMinimum(currentPlan, requiredPlan)) {
    return <>{children}</>;
  }

  const minLabel = PLAN_LABELS[requiredPlan];
  const defaultTitle = `${minLabel} feature`;
  const defaultDescription = `This feature requires the ${minLabel} plan or higher. Upgrade in Billing & Usage to unlock it.`;

  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] py-12 px-4 text-center bg-slate-50">
      <div className="rounded-full bg-slate-100 p-4 mb-4">
        <Icon className="w-10 h-10 text-slate-400" />
      </div>
      <h3 className="text-xl font-semibold text-slate-900 mb-2">{title ?? defaultTitle}</h3>
      <p className="text-sm text-slate-500 max-w-md mb-6">{description ?? defaultDescription}</p>
      <div className="flex items-center gap-3">
        <Button onClick={() => router.push('/settings?tab=billing-and-usage')}>
          Upgrade Plan
        </Button>
        <Button onClick={() => router.back()} variant="outline">
          Go Back
        </Button>
      </div>
    </div>
  );
};
